import { useState } from "react";
import { useAction, useQuery } from "wasp/client/operations";
import {
  downloadPlanPdf,
  emailWeeklyMealPlan,
  updateShoppingListItem,
  getShoppingListWithItems,
  generateMealPlan,
  regenerateMealPlan,
} from "wasp/client/operations";
import { trackEvent } from "../config/analytics";
import { toast } from "sonner";
import MyPlansPageView from "./MyPlansPage.view";
import { isDemoModeEnabled } from "../../utils/devMode";
import type { V32UITier } from '../../types/subscription';

// Business logic types
interface MealPlan {
  id: string;
  name: string;
  weekRange: string;
  createdAt: string;
  recipeCount: number;
  servings: number;
  avgCalories: number;
  status: "completed" | "in-progress" | "archived";
  thumbnail: string;
}

interface ShoppingListItem {
  id: string;
  ingredient: {
    id: string;
    name: string;
  };
  quantity: number;
  displayUnit?: string;
  displayQty?: number;
  section?: {
    id: string;
    name: string;
  } | null;
  checked: boolean;
  notes?: string;
  sortOrder: number;
}

interface ShoppingListData {
  id: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
  items: ShoppingListItem[];
}

interface MyPlansPageContainerProps {
  onViewPlan: (planId: string) => void;
  onCreateNew: () => void;
  currentTier: V32UITier;
  plans?: MealPlan[];
  usageStatus?: any;
  isGenerating?: boolean;
  onRefetchPlans?: () => Promise<void> | void;
}

/**
 * MyPlansPage Container - Centralizes all data logic, hooks, and business logic
 *
 * This centralizes:
 * - Wasp operations and data fetching (3 actions, 1 query)
 * - Complex filtering and search logic (search, status, date filters)
 * - Sophisticated sorting logic (8 different sort options)
 * - Shopping list integration and management
 * - PDF download and email functionality
 * - Status management and color coding
 * - All event handlers and business logic
 */
export default function MyPlansPageContainer({
  onViewPlan,
  onCreateNew,
  currentTier,
  plans,
  usageStatus,
  isGenerating,
  onRefetchPlans,
}: MyPlansPageContainerProps) {
  // Wasp hooks for data fetching and actions
  const doDownloadPdf = useAction(downloadPlanPdf as any);
  const doEmailPlan = useAction(emailWeeklyMealPlan as any);
  const doUpdateShoppingListItem = useAction(updateShoppingListItem as any);
  const doGeneratePlan = useAction(generateMealPlan as any);
  const doRegeneratePlan = useAction(regenerateMealPlan as any);
  const [pendingByPlan, setPendingByPlan] = useState<Record<string, { download?: boolean; email?: boolean; regenerate?: boolean }>>({});

  const setPending = (planId: string, key: 'download' | 'email' | 'regenerate', value: boolean) => {
    setPendingByPlan((prev) => ({
      ...prev,
      [planId]: { ...(prev[planId] || {}), [key]: value },
    }));
  };
  const trackEventAny = trackEvent as any;

  // Business logic state - Filtering and search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<MealPlan["status"] | "all">(
    "all",
  );
  const [dateFilter, setDateFilter] = useState<
    "all" | "this-week" | "this-month" | "custom"
  >("all");
  const [sortBy, setSortBy] = useState<
    | "date-desc"
    | "date-asc"
    | "name-asc"
    | "name-desc"
    | "recipes-desc"
    | "recipes-asc"
    | "calories-desc"
    | "calories-asc"
  >("date-desc");

  // Business logic state - UI management
  const [showFilters, setShowFilters] = useState(false);
  const [listPlanId, setListPlanId] = useState<string | null>(null);

  // Wasp query for shopping list data
  const { data: shoppingListData, refetch: refetchShoppingList } = useQuery(
    getShoppingListWithItems as any,
    { mealPlanId: listPlanId as any },
    { enabled: !!listPlanId },
  ) as { data: ShoppingListData | undefined; refetch: () => void };

  // Business logic: Fallback data for demo purposes
  const fallbackPlans: MealPlan[] = [
    {
      id: "1",
      name: "Mediterranean Week",
      weekRange: "Dec 9-15, 2024",
      createdAt: "2024-12-09",
      recipeCount: 7,
      servings: 14,
      avgCalories: 1850,
      status: "completed",
      thumbnail: "🥗",
    },
    {
      id: "2",
      name: "Comfort Food Classics",
      weekRange: "Dec 2-8, 2024",
      createdAt: "2024-12-02",
      recipeCount: 7,
      servings: 14,
      avgCalories: 2100,
      status: "completed",
      thumbnail: "🍲",
    },
    {
      id: "3",
      name: "Quick & Healthy",
      weekRange: "Nov 25-Dec 1, 2024",
      createdAt: "2024-11-25",
      recipeCount: 5,
      servings: 10,
      avgCalories: 1750,
      status: "archived",
      thumbnail: "🥙",
    },
    {
      id: "4",
      name: "Asian Fusion",
      weekRange: "Nov 18-24, 2024",
      createdAt: "2024-11-18",
      recipeCount: 6,
      servings: 12,
      avgCalories: 1950,
      status: "in-progress",
      thumbnail: "🍜",
    },
  ];

  // Business logic: Data processing
  const mealPlans: MealPlan[] =
    Array.isArray(plans) && plans.length
      ? (plans as any)
      : isDemoModeEnabled()
        ? fallbackPlans
        : [];

  // Business logic: Complex filtering logic
  const filteredPlans = mealPlans.filter((plan) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        plan.name.toLowerCase().includes(searchLower) ||
        plan.weekRange.toLowerCase().includes(searchLower) ||
        plan.status.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== "all" && plan.status !== statusFilter) {
      return false;
    }

    // Date filter
    if (dateFilter !== "all") {
      const planDate = new Date(plan.createdAt);
      const now = new Date();

      switch (dateFilter) {
        case "this-week":
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (planDate < weekAgo) return false;
          break;
        case "this-month":
          const monthAgo = new Date(
            now.getFullYear(),
            now.getMonth() - 1,
            now.getDate(),
          );
          if (planDate < monthAgo) return false;
          break;
      }
    }

    return true;
  });

  // Business logic: Complex sorting logic
  const sortedPlans = [...filteredPlans].sort((a, b) => {
    switch (sortBy) {
      case "date-desc":
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      case "date-asc":
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "recipes-desc":
        return b.recipeCount - a.recipeCount;
      case "recipes-asc":
        return a.recipeCount - b.recipeCount;
      case "calories-desc":
        return b.avgCalories - a.avgCalories;
      case "calories-asc":
        return a.avgCalories - b.avgCalories;
      default:
        return 0;
    }
  });

  // Business logic: Status utilities
  const getStatusColor = (status: MealPlan["status"]) => {
    switch (status) {
      case "completed":
        return "bg-sage text-white";
      case "in-progress":
        return "bg-coral text-ink";
      case "archived":
        return "bg-supporting text-white";
      default:
        return "bg-stone text-ink";
    }
  };

  const getStatusLabel = (status: MealPlan["status"]) => {
    switch (status) {
      case "completed":
        return "Completed";
      case "in-progress":
        return "Active";
      case "archived":
        return "Archived";
      default:
        return "Unknown";
    }
  };

  // Business logic: Action handlers
  const handleDownloadPdf = async (planId: string, planName: string) => {
    try {
      setPending(planId, 'download', true);
      trackEventAny("plan_download_pdf", {
        planId,
        planName,
        tier: currentTier,
      });

      const result: any = await doDownloadPdf({ id: planId });

      if (result?.success) {
        toast.success("PDF download started!");
      } else {
        throw new Error("Download failed");
      }
    } catch (error) {
      console.error("Failed to download PDF:", error);
      toast.error("Failed to download PDF. Please try again.");
    } finally { setPending(planId, 'download', false); }
  };

  const handleEmailPlan = async (planId: string, planName: string) => {
    try {
      setPending(planId, 'email', true);
      trackEventAny("plan_email", { planId, planName, tier: currentTier });

      const result: any = await doEmailPlan({ mealPlanId: planId });

      if (result?.success) {
        toast.success("Meal plan sent to your email!");
      } else {
        throw new Error("Email failed");
      }
    } catch (error) {
      console.error("Failed to email plan:", error);
      toast.error("Failed to send email. Please try again.");
    } finally { setPending(planId, 'email', false); }
  };

  const handleRegeneratePlan = async (planId: string, planName: string) => {
    try {
      setPending(planId, 'regenerate', true);
      trackEventAny("plan_regenerate", { planId, planName, tier: currentTier });
      await doRegeneratePlan({ mealPlanId: planId } as any);
      toast.success("Plan regeneration started");
      if (onRefetchPlans) {
        try { await onRefetchPlans(); } catch {}
      }
    } catch (error) {
      console.error("Failed to regenerate plan:", error);
      toast.error("Failed to regenerate plan. Please try again.");
    } finally { setPending(planId, 'regenerate', false); }
  };

  const handleViewShoppingList = (planId: string, planName: string) => {
    trackEventAny("plan_view_shopping_list", {
      planId,
      planName,
      tier: currentTier,
    });
    setListPlanId(planId);
  };

  const handleCloseShoppingList = () => {
    setListPlanId(null);
  };

  const handleToggleShoppingItem = async (itemId: string, checked: boolean) => {
    try {
      await doUpdateShoppingListItem({
        itemId,
        updates: { checked },
      });

      // Optimistically update local state or refetch
      await refetchShoppingList();

      trackEventAny("shopping_item_toggle", {
        itemId,
        checked,
        tier: currentTier,
      });
    } catch (error) {
      console.error("Failed to update shopping item:", error);
      toast.error("Failed to update item. Please try again.");
    }
  };

  // Business logic: Filter handlers
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    trackEventAny("plans_search", {
      query: value,
      tier: currentTier,
    });
  };

  const handleStatusFilterChange = (status: MealPlan["status"] | "all") => {
    setStatusFilter(status);
    trackEventAny("plans_filter_status", {
      status,
      tier: currentTier,
    });
  };

  const handleDateFilterChange = (
    dateFilter: "all" | "this-week" | "this-month" | "custom",
  ) => {
    setDateFilter(dateFilter);
    trackEventAny("plans_filter_date", {
      dateFilter,
      tier: currentTier,
    });
  };

  const handleSortChange = (sort: typeof sortBy) => {
    setSortBy(sort);
    trackEventAny("plans_sort", {
      sortBy: sort,
      tier: currentTier,
    });
  };

  const handleViewPlan = (planId: string, planName: string) => {
    trackEventAny("plan_view", {
      planId,
      planName,
      tier: currentTier,
    });
    onViewPlan(planId);
  };

  const handleCreateNew = () => {
    trackEventAny("plan_create_new", {
      tier: currentTier,
    });
    onCreateNew();
  };

  const handleCreateQuick = async () => {
    // Note: isGenerating comes from props, managed by parent component
    trackEventAny("plan_create_quick_attempt", {
      tier: currentTier,
    });

    try {
      const newPlan = await doGeneratePlan({ quickMode: true });
      toast.success("Quick plan created!");
      onViewPlan((newPlan as any).mealPlan.id);
    } catch (e: any) {
      console.error("Failed to create quick plan:", e);
      const msg = (e?.message || "").toLowerCase();
      if (msg.includes("weekly") || msg.includes("limit")) {
        toast.error(
          "You have reached your weekly meal plan limit. Please upgrade your subscription to generate more plans.",
        );
      } else {
        toast.error("Failed to create quick plan. Please try again.");
      }
      trackEventAny("plan_create_quick_error", {
        tier: currentTier,
        error: String(e?.message || e),
      });
    }
  };

  // Computed values
  const hasActiveFilters =
    Boolean(searchTerm) || statusFilter !== "all" || dateFilter !== "all";
  const totalPlansCount = mealPlans.length;
  const filteredPlansCount = sortedPlans.length;
  const hasShoppingList =
    !!shoppingListData && shoppingListData.items.length > 0;

  // Filter and sort options for the UI
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "completed", label: "Completed" },
    { value: "in-progress", label: "Active" },
    { value: "archived", label: "Archived" },
  ];

  const dateOptions = [
    { value: "all", label: "All Time" },
    { value: "this-week", label: "This Week" },
    { value: "this-month", label: "This Month" },
  ];

  const sortOptions = [
    { value: "date-desc", label: "Newest First" },
    { value: "date-asc", label: "Oldest First" },
    { value: "name-asc", label: "Name A-Z" },
    { value: "name-desc", label: "Name Z-A" },
    { value: "recipes-desc", label: "Most Recipes" },
    { value: "recipes-asc", label: "Fewest Recipes" },
    { value: "calories-desc", label: "Highest Calories" },
    { value: "calories-asc", label: "Lowest Calories" },
  ];

  // Map all props to the view
  const viewProps = {
    // Data
    plans: sortedPlans,
    shoppingListData,
    currentTier,
    usageStatus,

    // State
    searchTerm,
    statusFilter,
    dateFilter,
    sortBy,
    showFilters,
    listPlanId,

    // Computed values
    isGenerating: isGenerating ?? false,
    hasActiveFilters,
    totalPlansCount,
    filteredPlansCount,
    hasShoppingList,

    // Options
    statusOptions,
    dateOptions,
    sortOptions,

    // Utility functions
    getStatusColor,
    getStatusLabel,

    // Event handlers - Search and filters
    onSearchChange: handleSearchChange,
    onStatusFilterChange: handleStatusFilterChange,
    onDateFilterChange: handleDateFilterChange,
    onSortChange: (sort: string) => handleSortChange(sort as any),
    onToggleFilters: () => setShowFilters(!showFilters),

    // Event handlers - Plan actions
    onViewPlan: handleViewPlan,
    onCreateNew: handleCreateNew,
    onCreateQuick: handleCreateQuick,
    onDownloadPdf: handleDownloadPdf,
    onEmailPlan: handleEmailPlan,
    onRegeneratePlan: handleRegeneratePlan,
    pendingByPlan,

    // Event handlers - Shopping list
    onViewShoppingList: handleViewShoppingList,
    onCloseShoppingList: handleCloseShoppingList,
    onToggleShoppingItem: handleToggleShoppingItem,
    onRefetchShoppingList: refetchShoppingList,
  };

  return <MyPlansPageView {...viewProps} />;
}

// Export types for the view component
export type { MealPlan, ShoppingListItem, ShoppingListData };
